<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent max-width=600 content-class="round-2xl")
  v-card
    v-card-title.px-4
      v-spacer
      .text-xl.font-bold {{ $t('assign_item', { item:itemText }) }}
      v-spacer
      v-btn(icon color="neutral700" @click="closeDialog")
        v-icon mdi-close
    v-divider
    div.py-6.px-10
      .text-md.font-semibold.mb-4 {{ $t('assign_item_description', { item:itemText }) }}
      v-card.pa-4.round-lg(outlined)
        v-row(no-gutters align="center")
          v-col(cols="2")
            v-avatar(color="basic300")
              v-img(v-if="parentReview.reviewer_photo_url" :src="parentReview.reviewer_photo_url" :alt="`${parentReview.reviewer_name}-profile-photo`")
              span.text-md.font-semibold.neutral500--text(v-else) {{ getInitial(name) }}
          v-col
            .text-sm.font-semibold {{ parentReview.reviewer_name }}
            .text-xs.font-normal.neutral500--text {{ convertDateTime(parentReview.posted_at) }}
          v-col.d-flex.align-center
            v-icon.mr-2 $yzi-google-icon
            star-generator(:totalStar="5" :star="parentReview.star" :size="20" classes="mx-1")
      .text-sm.font-semibold.neutral500--text.mt-6.mb-2 {{ $t('select_item', { item:itemText }) }}
      v-autocomplete.round-lg(v-model="selectedCustomer" :placeholder="$t('select_item_placeholder', { item:itemText })" :items="customers" :loading="isFetching" item-text="name" item-value="id" outlined :search-input.sync="searchCustomer") 
        template(v-slot:item="data")
          v-row(no-gutters)
            v-col
              .text-md.neutral700--text {{ data.item.name }}
            v-spacer
            v-col.d-flex.flex-column.text-right
              .text-xs.neutral500--text(v-if="data.item.email") {{ data.item.email }}
              .text-xs.neutral500--text(v-if="data.item.phone") {{ data.item.phone | phoneFormat }}
    v-divider
    v-card-actions.px-6
      v-btn.button-md.neutral700--text.px-0.underline(text @click="closeDialog") {{ $t('cancel') }}
      v-spacer
      v-btn.button-md(@click="save" :loading="isLoading" color="neutral700" :disabled="!canProceed" :dark="canProceed" depressed) {{ $t('save') }}
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import StarGenerator from '@/components/review/general/StarGenerator.vue'

export default {
  name: 'ReviewAssignCustomerDialog',
  components: {
    StarGenerator,
  },
  props: {
    open: {
      type: Boolean,
    },
    parentReview: {
      type: Object,
      default: () => {
        return {}
      }
    },
    os: {
      type: String,
      default: 'review',
      validator(value) {
        return ['clinic', 'donation', 'connect', 'review'].includes(value)
      }
    },
  },
  data() {
    return {
      isFetching: false,
      isLoading: false,
      selectedCustomer: null,
      customers: [],
      searchCustomer: null
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    canProceed() {
      return this.selectedCustomer != null
    },
    itemText() {
      switch (this.os) {
        case 'clinic':
          return this.$t('patient').toLowerCase()

        case 'review':
          return this.$t('customer').toLowerCase()
      
        default:
          return ''
      }
    }
  },
  async mounted () {
    await this.fetchCustomers()
    if (this.parentReview.customer) {
      this.customers.push(this.parentReview.customer)
      this.selectedCustomer = this.parentReview.customer.id
    }
  },
  watch: {
    searchCustomer() {
      this.searchStart()
    },
  },
  methods: {
    ...mapActions(['showMessage']),
    closeDialog() {
      this.$emit('cancel')
      this.showDialog = false
    },
    async save() {
      this.isLoading = true
      try {
        const { data } = await this.$axios.patch(`/reviews/${this.company.slug}/google_reviews/${this.parentReview.id}?expand=customer`, { customer: this.selectedCustomer })
        this.$emit('update', data)
        this.showMessage(['success', this.$t('success_message.successfully_updated')])
        this.closeDialog()
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isLoading = false
      }
    },
    getInitial(value) {
      let name = value
      let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
      let initials = [...name.matchAll(rgx)] || [];
      initials = ((initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')).toUpperCase();
      return initials
    },
    convertDateTime(value) {
      const isoDateTime = DateTime.fromISO(value)
      const date = isoDateTime.toFormat('dd MMM yyyy')
      const time = isoDateTime.toFormat('hh:mm a')
      return `${date}, ${time}`
    },
    searchStart: debounce(function () {
      this.fetchCustomers(this.searchCustomer)
    }, 500),
    async fetchCustomers() {
      this.isFetching = true
      const params = {
        review_invited: true,
        search: this.searchCustomer
      }
      try {
        const { data } = await this.$axios.get(`/connect/${this.company.slug}/customers`, { params })
        this.customers = data.results
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    }
  },
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.no-padding-x {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.selected-button {
  background: #eeeeee !important;
  border-radius: 16px;
}
::v-deep svg.v-icon.notranslate.v-data-table-header__icon {
  height: 8px !important;
  width: 8px !important;
  margin-left: 8px;
}
.file-change-layout {
  position: relative;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
.pre-wrap {
  white-space: pre-wrap;
}
.sticky {
  top: 1vh;
  position: sticky;
}
</style>